import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Clock, Route, Package, Loader2, CalendarDays, CheckCircle2, CircleDot, CalendarClock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { ProLocked, CompanyPicker } from "../components/ProGate";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { canPlan, hasPro, isProRequired, useProScope } from "../lib/pro";

const fmt = (n) => (n == null ? "—" : (Math.round(Number(n) * 10) / 10).toLocaleString());

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const scope = useProScope(user);
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (user && hasPro(user) && !canPlan(user)) nav("/planning", { replace: true }); }, [user, nav]);

  useEffect(() => {
    if (!canPlan(user) || scope.needsCompany) return;
    setLoading(true); setErr(""); setLocked(false);
    api.get("/pro/dashboard", { params: { range, ...scope.params } })
      .then(({ data }) => setData(data))
      .catch((e) => { setData(null); if (isProRequired(e)) setLocked(true); else setErr(apiError(e)); })
      .finally(() => setLoading(false));
  }, [range, user, scope.cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = [
    { key: "reports_sent", label: t("Reports sent"), icon: FileText },
    { key: "hours_logged", label: t("Hours logged"), icon: Clock, suffix: "h" },
    { key: "km_driven", label: t("Km driven"), icon: Route, suffix: " km" },
    { key: "parts_used", label: t("Parts used"), icon: Package },
    { key: "jobs_planned", label: t("Jobs planned"), icon: CalendarDays },
    { key: "jobs_done", label: t("Jobs done"), icon: CheckCircle2 },
    { key: "jobs_open", label: t("Open jobs"), icon: CircleDot },
    { key: "jobs_today", label: t("Jobs today"), icon: CalendarClock },
  ];

  if (!hasPro(user)) return <><PageHeader title={t("Dashboard")} /><ProLocked /></>;

  return (
    <>
      <PageHeader title={t("Dashboard")} subtitle={t("Your company at a glance")} />
      {scope.isSA && <CompanyPicker scope={scope} />}
      {scope.needsCompany ? <p className="text-zinc-500 px-4 md:px-0 mt-4 text-sm">{t("Choose a company to view its dashboard.")}</p>
        : locked ? <ProLocked /> : (
        <div className="px-4 md:px-0 pt-4">
          <div className="inline-flex rounded-xl border border-zinc-800 overflow-hidden" data-testid="dashboard-range-toggle">
            {[["week", t("Week")], ["month", t("Month")]].map(([r, label]) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`dashboard-range-${r}`}
                className={`px-4 py-2 text-sm font-bold transition-colors ${range === r ? "bg-[#F26722] text-white" : "bg-[#18181B] text-zinc-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          {err && <p className="text-sm text-red-400 mt-3" data-testid="dashboard-error">{err}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mt-5">
            {cards.map((c) => (
              <div key={c.key} data-testid={`dashboard-card-${c.key}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
                <div className="w-10 h-10 rounded-xl bg-[#F26722]/15 flex items-center justify-center"><c.icon size={20} className="text-[#F26722]" /></div>
                <p className="text-3xl font-extrabold text-white mt-4" data-testid={`dashboard-value-${c.key}`}>
                  {loading ? <Loader2 className="animate-spin text-zinc-600" size={22} /> : <>{fmt(data?.[c.key])}{c.suffix && <span className="text-base font-bold text-zinc-500">{c.suffix}</span>}</>}
                </p>
                <p className="text-sm text-zinc-400 mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
